import type { Todo } from "../../domain/entities.ts";

export type WeeklyReviewReason = "overdue" | "inactive" | "blocked" | "unscheduled" | "uncollected" | "recently_completed";

export interface WeeklyReviewCandidate { task: Todo; reasons: WeeklyReviewReason[] }
export interface WeeklyReviewState { startedAt: number; updatedAt: number; taskIds: string[]; reviewedIds: string[]; completedAt: number | null; scope?: string; decisions?: Record<string, string> }
export interface WeeklyReviewReminder { enabled: boolean; weekday: number; hour: number; lastPromptWeek: string | null }

const DAY = 86_400_000;

export function weeklyReviewCandidates(tasks: Todo[], date: string, now: number, inactiveDays = 30): WeeklyReviewCandidate[] {
  const visible = tasks.filter(task => !task.deleted && !task.archivedAt);
  const byId = new Map(visible.map(task => [task.id, task]));
  const score = (reasons: WeeklyReviewReason[]): number => Math.min(...reasons.map(reason => ({ overdue: 0, blocked: 1, inactive: 2, unscheduled: 3, uncollected: 4, recently_completed: 5 })[reason]));
  return visible.flatMap(task => {
    const reasons: WeeklyReviewReason[] = [];
    if (!task.completed && !!task.dueDate && task.dueDate < date) reasons.push("overdue");
    if (!task.completed && (task.dependencyIds || []).some(id => !byId.get(id)?.completed)) reasons.push("blocked");
    if (!task.completed && task.updatedAt < now - inactiveDays * DAY) reasons.push("inactive");
    if (!task.completed && !task.dueDate && !task.startDate && !task.startAt) reasons.push("unscheduled");
    if (!task.completed && !task.categoryId) reasons.push("uncollected");
    if (task.completed && (task.completedAt || task.updatedAt) >= now - 7 * DAY) reasons.push("recently_completed");
    return reasons.length ? [{ task, reasons }] : [];
  }).sort((left, right) => score(left.reasons) - score(right.reasons) || left.task.position - right.task.position || left.task.title.localeCompare(right.task.title));
}

export function normalizeWeeklyReview(value: Partial<WeeklyReviewState> | null | undefined): WeeklyReviewState | null {
  if (!value?.startedAt || !Array.isArray(value.taskIds)) return null;
  const taskIds = [...new Set(value.taskIds.filter(id => typeof id === "string"))];
  const decisions = value.decisions && typeof value.decisions === "object" ? Object.fromEntries(Object.entries(value.decisions).filter(([id, action]) => taskIds.includes(id) && typeof action === "string")) : {};
  return { startedAt: value.startedAt, updatedAt: value.updatedAt || value.startedAt, taskIds, reviewedIds: [...new Set((value.reviewedIds || []).filter(id => taskIds.includes(id)))], completedAt: value.completedAt || null, scope: typeof value.scope === "string" ? value.scope : undefined, decisions };
}

export function startWeeklyReview(candidates: WeeklyReviewCandidate[], now: number): WeeklyReviewState {
  return { startedAt: now, updatedAt: now, taskIds: candidates.map(item => item.task.id), reviewedIds: [], completedAt: candidates.length ? null : now };
}

export function advanceWeeklyReview(state: WeeklyReviewState, taskId: string, now: number, action = "keep"): WeeklyReviewState {
  const reviewedIds = [...new Set([...state.reviewedIds, taskId])];
  return { ...state, reviewedIds, decisions: { ...state.decisions, [taskId]: action }, updatedAt: now, completedAt: reviewedIds.length >= state.taskIds.length ? now : null };
}

export function currentWeeklyReviewTask(state: WeeklyReviewState, availableIds: Set<string>): string | null {
  return state.taskIds.find(id => !state.reviewedIds.includes(id) && availableIds.has(id)) || null;
}

export function weekKey(date: Date): string {
  const value = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  value.setUTCDate(value.getUTCDate() + 4 - (value.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(value.getUTCFullYear(), 0, 1));
  return `${value.getUTCFullYear()}-W${String(Math.ceil((((value.getTime() - yearStart.getTime()) / DAY) + 1) / 7)).padStart(2, "0")}`;
}

export function weeklyReviewReminderDue(settings: WeeklyReviewReminder, now: Date): boolean {
  return settings.enabled && now.getDay() === settings.weekday && now.getHours() >= settings.hour && settings.lastPromptWeek !== weekKey(now);
}
